import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBullhorn, faBell, faExclamationTriangle,
  faCircleInfo, faUsers, faShieldAlt, faGlobe,
  faCalendarDays, faRefresh
} from '@fortawesome/free-solid-svg-icons';
import { BASE_URL } from '../utils/constants';

/* ── metadata igual que en admin ── */
const NIVEL_META = {
  urgente:      { label: 'Urgente',      color: 'bg-red-100 text-red-700 border-red-300',      icon: faExclamationTriangle, bar: 'bg-red-500',   banner: 'bg-red-50 border-red-200' },
  comunicado:   { label: 'Comunicado',   color: 'bg-blue-100 text-blue-700 border-blue-300',    icon: faBullhorn,           bar: 'bg-blue-500',  banner: 'bg-blue-50 border-blue-200' },
  notificacion: { label: 'Notificación', color: 'bg-green-100 text-green-700 border-green-300', icon: faBell,               bar: 'bg-green-500', banner: 'bg-green-50 border-green-200' },
};

const DEST_META = {
  todos:      { label: 'Todos',      icon: faGlobe,     color: 'bg-indigo-100 text-indigo-700' },
  inquilinos: { label: 'Inquilinos', icon: faUsers,     color: 'bg-amber-100 text-amber-700'   },
  seguridad:  { label: 'Seguridad',  icon: faShieldAlt, color: 'bg-slate-100 text-slate-700'   },
};

const fmtDatetime = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  const months = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
  return `${dt.getDate()} ${months[dt.getMonth()]} ${dt.getFullYear()} · ${String(dt.getHours()).padStart(2,'0')}:${String(dt.getMinutes()).padStart(2,'0')}`;
};

const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(d + 'T12:00:00');
  const months = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
  return `${dt.getDate()} ${months[dt.getMonth()]} ${dt.getFullYear()}`;
};

/**
 * ComunicadosView — componente reutilizable para inquilinos y seguridad
 * Props:
 *   role: 'inquilinos' | 'seguridad'
 *   NavigationComponent: el componente de nav correspondiente
 *   accentColor: clase de color para el encabezado (ej. 'from-indigo-500 to-blue-600')
 */
const ComunicadosView = ({ role, NavigationComponent, accentColor = 'from-indigo-500 to-purple-600' }) => {
  const [comunicados, setComunicados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [filterNivel, setFilterNivel] = useState('');

  const fetchComunicados = useCallback(async () => {
    try {
      const r = await axios.get(`${BASE_URL}/comunicados/role/${role}`);
      setComunicados(r.data);
      setLastUpdate(new Date());
    } catch { /* silencioso */ }
    finally { setLoading(false); }
  }, [role]);

  useEffect(() => {
    fetchComunicados();
    const interval = setInterval(fetchComunicados, 60000); // refresh cada 60s
    return () => clearInterval(interval);
  }, [fetchComunicados]);

  const filtered = filterNivel ? comunicados.filter(c => c.nivel === filterNivel) : comunicados;
  const urgentes = comunicados.filter(c => c.nivel === 'urgente');

  if (loading) return (
    <>
      <NavigationComponent />
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-12 w-12 rounded-full border-b-2 border-indigo-600"></div>
      </div>
    </>
  );

  return (
    <>
      <NavigationComponent />

      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50 p-4 sm:p-6 lg:p-8">
        <div className="max-w-3xl mx-auto">

          {/* Header */}
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6 flex items-center gap-4">
            <div className={`p-3 bg-gradient-to-br ${accentColor} rounded-xl shadow`}>
              <FontAwesomeIcon icon={faBullhorn} className="h-8 w-8 text-white" />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-800">Comunicados</h1>
              <p className="text-sm text-gray-500 mt-0.5">
                {comunicados.length} comunicado(s) activo(s)
                {lastUpdate && (
                  <span> · Actualizado {String(lastUpdate.getHours()).padStart(2,'0')}:{String(lastUpdate.getMinutes()).padStart(2,'0')}</span>
                )}
              </p>
            </div>
            <button onClick={fetchComunicados}
              className="flex items-center gap-2 text-sm text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 px-3 py-2 rounded-lg transition-colors">
              <FontAwesomeIcon icon={faRefresh} className="h-4 w-4" />
              Actualizar
            </button>
          </div>

          {/* Banner urgentes */}
          {urgentes.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-4 mb-5 flex items-center gap-3">
              <FontAwesomeIcon icon={faExclamationTriangle} className="h-6 w-6 text-red-500 shrink-0" />
              <div>
                <p className="text-sm font-bold text-red-700">
                  {urgentes.length === 1 ? 'Hay 1 comunicado urgente' : `Hay ${urgentes.length} comunicados urgentes`}
                </p>
                <p className="text-xs text-red-500 mt-0.5">Por favor léelos y toma las acciones necesarias</p>
              </div>
            </div>
          )}

          {/* Filtro */}
          <div className="flex gap-3 mb-5 flex-wrap">
            {[{ val: '', label: 'Todos' }, { val: 'urgente', label: 'Urgentes' }, { val: 'comunicado', label: 'Comunicados' }, { val: 'notificacion', label: 'Notificaciones' }].map(f => (
              <button key={f.val} onClick={() => setFilterNivel(f.val)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors
                  ${filterNivel === f.val ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-300 hover:border-indigo-300'}`}>
                {f.label}
              </button>
            ))}
          </div>

          {/* Lista */}
          <div className="space-y-4">
            {filtered.length === 0 ? (
              <div className="bg-white rounded-2xl shadow p-16 text-center text-gray-400">
                <FontAwesomeIcon icon={faBullhorn} className="h-12 w-12 mb-3 opacity-30" />
                <p className="text-lg font-medium">No hay comunicados</p>
              </div>
            ) : (
              filtered.map(c => {
                const nivel = NIVEL_META[c.nivel] || NIVEL_META.comunicado;
                const dest = DEST_META[c.destinatario] || DEST_META.todos;
                return (
                  <div key={c.id_comunicado} className="bg-white rounded-2xl shadow overflow-hidden">
                    <div className={`h-1.5 ${nivel.bar}`}></div>
                    <div className="p-5">
                      <div className="flex flex-wrap gap-2 mb-3">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${nivel.color}`}>
                          <FontAwesomeIcon icon={nivel.icon} className="h-3 w-3" />
                          {nivel.label}
                        </span>
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${dest.color}`}>
                          <FontAwesomeIcon icon={dest.icon} className="h-3 w-3" />
                          Para: {dest.label}
                        </span>
                      </div>

                      <h3 className="text-base font-bold text-gray-800 mb-2">{c.titulo}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">{c.contenido}</p>

                      <div className="mt-4 pt-3 border-t border-gray-50 space-y-1.5">
                        {(c.fecha_inicio || c.fecha_fin) && (
                          <div className="flex items-center gap-1">
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-700">
                              <FontAwesomeIcon icon={faCalendarDays} className="h-3 w-3" />
                              Vigente: {c.fecha_inicio ? fmtDate(c.fecha_inicio) : '∞'} – {c.fecha_fin ? fmtDate(c.fecha_fin) : '∞'}
                            </span>
                          </div>
                        )}
                        <div className="flex items-center gap-1 text-xs text-gray-400">
                          <FontAwesomeIcon icon={faCalendarDays} className="h-3 w-3" />
                          <span>{fmtDatetime(c.fecha_creacion)}</span>
                          <span className="mx-1">·</span>
                          <span>{c.admin_nombre} {c.admin_apellido}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

        </div>
      </div>
    </>
  );
};

export default ComunicadosView;
